// src/pages/ArticleDetailPage.js

import React, { useContext, useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { articleService } from '../api';
import SocialShare from '../components/common/SocialShare';
import Button from '../components/common/Button';
import useApi from '../hooks/useApi';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Configuration pour react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Styled components
const ArticleSection = styled.section`
  padding: 5rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 0.5rem;
  }
`;

const ArticleHeader = styled.header`
  margin-bottom: 3rem;
`;

const ArticleTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  
  div {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 0.5rem;
      color: ${props => props.theme.primary};
    }
  }
`;

const EngagementStats = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  button {
    display: flex;
    align-items: center;
    background: transparent;
    border: none;
    color: ${props => props.theme.text};
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: ${props => props.theme.backgroundSecondary};
    }
    
    &.active {
      color: ${props => props.theme.primary};
    }
    
    i {
      margin-right: 0.5rem;
      font-size: 1.2rem;
    }
  }
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const ArticleContent = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.8;
  font-size: 1.1rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.8rem;
    margin: 2rem 0 1rem;
    color: ${props => props.theme.primary};
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 1.8rem 0 1rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  blockquote {
    border-left: 4px solid ${props => props.theme.primary};
    padding: 1rem 1.5rem;
    margin: 1.5rem 0;
    background: ${props => props.theme.backgroundSecondary};
    font-style: italic;
  }
  
  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 1.5rem 0;
  }
  
  pre {
    background: ${props => props.theme.backgroundSecondary};
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 1.5rem 0;
  }
  
  code {
    background: ${props => props.theme.backgroundSecondary};
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
  }
  
  iframe {
    width: 100%;
    height: 450px;
    border-radius: 8px;
    margin: 1.5rem 0;
    border: none;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 3rem 0;
`;

const TagItem = styled(Link)`
  display: inline-block;
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.primary + '30'};
  }
`;

const AuthorBox = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 2rem;
  margin: 3rem 0;
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const AuthorImage = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-image: url(${props => props.src || '/default-avatar.jpg'});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
`;

const AuthorBio = styled.p`
  color: ${props => props.theme.text};
  opacity: 0.8;
  margin-bottom: 1rem;
`;

const CommentsSection = styled.div`
  margin: 3rem 0;
`;

const CommentTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.accent};
  }
`;

const CommentList = styled.div`
  margin-top: 2rem;
`;

const CommentCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const CommentAuthor = styled.div`
  font-weight: bold;
  color: ${props => props.theme.text};
`;

const CommentDate = styled.div`
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 0.9rem;
`;

const CommentContent = styled.p`
  color: ${props => props.theme.text};
  line-height: 1.6;
`;

const CommentForm = styled.form`
  margin-top: 2rem;
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
`;

const CommentInput = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  min-height: 120px;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const CommentNameInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const CommentFormHeader = styled.div`
  margin-bottom: 1rem;
  font-size: 1.2rem;
  color: ${props => props.theme.text};
`;

const RelatedArticles = styled.div`
  margin: 3rem 0;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const RelatedTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.accent};
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const DownloadSection = styled.div`
  margin: 2rem 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
`;

const DownloadCounter = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.text};
  opacity: 0.8;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background: ${props => props.theme.backgroundSecondary};
  
  i {
    margin-right: 0.5rem;
    color: ${props => props.theme.primary};
  }
`;

const PdfContainer = styled.div`
  margin: 2rem 0;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 8px;
  padding: 1rem;
  max-width: 100%;
  overflow-x: auto;
`;

const PdfControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
  
  .pdf-navigation {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  
  .page-count {
    margin: 0 0.5rem;
  }
  
  button {
    background: ${props => props.theme.primary};
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
      background: ${props => props.theme.primary}dd;
    }
    
    &:disabled {
      background: ${props => props.theme.text}30;
      cursor: not-allowed;
    }
    
    i {
      margin: 0 0.2rem;
    }
  }
`;

const VideoContainer = styled.div`
  margin: 2rem 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  background: ${props => props.theme.backgroundSecondary};
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  
  iframe, video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ImageGalleryContainer = styled.div`
  margin: 2rem 0;
  
  .gallery-image {
    width: 100%;
    border-radius: 8px;
    margin-bottom: 1rem;
    box-shadow: ${props => props.theme.card.shadow};
  }
  
  .image-caption {
    color: ${props => props.theme.text};
    opacity: 0.8;
    text-align: center;
    margin-bottom: 2rem;
  }
`;

const DocumentEmbed = styled.div`
  margin: 2rem 0;
  border-radius: 8px;
  overflow: hidden;
  height: 800px;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ShareBar = styled.div`
  position: sticky;
  top: 100px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  float: left;
  margin-left: -80px;
  
  @media (max-width: 1200px) {
    position: static;
    float: none;
    margin-left: 0;
    flex-direction: row;
    justify-content: center;
    margin-bottom: 2rem;
  }
  
  button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.theme.backgroundSecondary};
    color: ${props => props.theme.text};
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: ${props => props.theme.primary};
      color: white;
    }
    
    i {
      font-size: 1rem;
    }
  }
`;

const ArticleDetailPage = () => {
  const { articleSlug } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  
  // Références pour le PDF
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const pdfRef = useRef(null);
  
  // États pour l'engagement
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentContent, setCommentContent] = useState('');
  const [commentAuthor, setCommentAuthor] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloads, setDownloads] = useState(0);
  
  // Charger les détails de l'article
  const { 
    data: article, 
    loading: articleLoading, 
    error: articleError,
    refetch: refetchArticle
  } = useApi(() => articleService.getById(articleSlug), [articleSlug]);
  
  // Charger les articles associés
  const { 
    data: relatedArticles, 
    loading: relatedLoading 
  } = useApi(() => article ? articleService.getAll({ 
    category__slug: article.categories?.[0]?.slug, 
    exclude: article.id,
    is_published: true,
    limit: 3
  }) : null, [article], false);

  // Charger les commentaires, likes et téléchargements de l'article
  useEffect(() => {
    if (article?.id) {
      // Initialisation des likes depuis localStorage
      const articleLikes = localStorage.getItem(`article_${article.id}_likes`) || 0;
      setLikes(Number(articleLikes));
      
      // Vérifie si l'utilisateur a déjà liké
      const userLiked = localStorage.getItem(`article_${article.id}_liked`) === 'true';
      setHasLiked(userLiked);
      
      // Chargement des commentaires depuis localStorage
      const savedComments = localStorage.getItem(`article_${article.id}_comments`);
      if (savedComments) {
        setComments(JSON.parse(savedComments));
      }
      
      // Charger le nombre de téléchargements
      const downloadCount = localStorage.getItem(`article_${article.id}_downloads`) || 0;
      setDownloads(Number(downloadCount));
    }
  }, [article]);
  
  // Mise à jour du titre de la page
  useEffect(() => {
    if (article) {
      document.title = `${article.title} | PropentaTech`;
    }
    return () => {
      document.title = 'PropentaTech';
    };
  }, [article]);
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Gestion des fonctions pour le PDF
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }
  
  function changePage(offset) {
    setPageNumber(prevPageNumber => Math.max(1, Math.min(numPages, prevPageNumber + offset)));
  }
  
  function previousPage() {
    changePage(-1);
  }
  
  function nextPage() {
    changePage(1);
  }
  
  // Fonction pour gérer les likes
  const handleLike = () => {
    if (!article) return;
    
    // Toggle like
    const newLikedState = !hasLiked;
    setHasLiked(newLikedState);
    
    // Mettre à jour le compteur de likes
    const newLikes = newLikedState ? likes + 1 : likes - 1;
    setLikes(newLikes);
    
    // Stocker dans localStorage
    localStorage.setItem(`article_${article.id}_likes`, newLikes);
    localStorage.setItem(`article_${article.id}_liked`, newLikedState);
    
    // Optionnel: Envoyer au serveur
    // articleService.likeArticle(article.id, newLikedState);
  };
  
  // Fonction pour soumettre un commentaire
  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (!commentContent.trim() || !commentAuthor.trim() || !article) return;
    
    setIsSubmitting(true);
    
    // Créer le nouveau commentaire
    const newComment = {
      id: Date.now(),
      author: commentAuthor,
      content: commentContent,
      created_at: new Date().toISOString()
    };
    
    // Ajouter le commentaire à la liste
    const updatedComments = [...comments, newComment];
    setComments(updatedComments);
    
    // Stocker dans localStorage
    localStorage.setItem(`article_${article.id}_comments`, JSON.stringify(updatedComments));
    
    // Optionnel: Envoyer au serveur
    // articleService.addComment(article.id, newComment);
    
    // Réinitialiser le formulaire
    setCommentContent('');
    setIsSubmitting(false);
  };
  
  // Fonction pour gérer le téléchargement
  const handleDownload = () => {
    if (!article) return;
    
    // Incrémenter le nombre de téléchargements
    const newDownloads = downloads + 1;
    setDownloads(newDownloads);
    
    // Stocker dans localStorage
    localStorage.setItem(`article_${article.id}_downloads`, newDownloads);
    
    // Effectuer le téléchargement selon le type d'article
    let downloadUrl = '';
    
    if (article.article_type === 'pdf' && article.pdf_file) {
      downloadUrl = article.pdf_file;
    } else if (article.article_type === 'document' && article.document_file) {
      downloadUrl = article.document_file;
    } else if (article.article_type === 'image' && article.featured_image) {
      downloadUrl = article.featured_image;
    } else if (article.download_url) {
      downloadUrl = article.download_url;
    }
    
    if (downloadUrl) {
      // Créer un lien temporaire pour le téléchargement
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = article.title || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Optionnel: Envoyer au serveur
      // articleService.trackDownload(article.id);
    }
  };
  
  // Fonction pour partager l'article
  const handleShare = (platform) => {
    if (!article) return;
    
    const articleUrl = window.location.href;
    const articleTitle = article.title;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(articleTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(articleTitle + ' ' + articleUrl)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(articleTitle)}&body=${encodeURIComponent(articleUrl)}`;
        break;
      default:
        // Utiliser l'API de partage native si disponible
        if (navigator.share) {
          navigator.share({
            title: articleTitle,
            url: articleUrl
          });
          return;
        }
    }
    
    // Ouvrir la fenêtre de partage
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };
  
  // Rendu en fonction du type d'article
  const renderArticleContent = () => {
    if (!article) return null;
    
    switch (article.article_type) {
      case 'pdf':
        return (
          <PdfContainer theme={theme} ref={pdfRef}>
            <PdfControls theme={theme}>
              <div className="pdf-navigation">
                <button onClick={previousPage} disabled={pageNumber <= 1}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span className="page-count">
                  {t('page')} {pageNumber} / {numPages || '?'}
                </span>
                <button onClick={nextPage} disabled={pageNumber >= numPages}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              <div>
                <button onClick={handleDownload}>
                  <i className="fas fa-download"></i> {t('download')}
                </button>
              </div>
            </PdfControls>
            <Document
              file={article.pdf_file}
              onLoadSuccess={onDocumentLoadSuccess}
              error={<div>{t('pdfError')}</div>}
              loading={<div>{t('loadingPdf')}</div>}
            >
              <Page 
                pageNumber={pageNumber} 
                renderTextLayer={true}
                renderAnnotationLayer={true}
                width={pdfRef.current ? pdfRef.current.offsetWidth - 30 : 800}
              />
            </Document>
            <DownloadSection theme={theme}>
              <DownloadCounter theme={theme}>
                <i className="fas fa-download"></i>
                <span>{downloads} {t('downloads')}</span>
              </DownloadCounter>
            </DownloadSection>
          </PdfContainer>
        );
        
      case 'video':
        return (
          <VideoContainer theme={theme}>
            {article.video_url && (
              article.video_url.includes('youtube.com') || article.video_url.includes('youtu.be') ?
                <iframe
                  src={article.video_url.replace('watch?v=', 'embed/')}
                  title={article.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                :
                article.video_url.includes('vimeo.com') ?
                  <iframe
                    src={article.video_url.replace('vimeo.com', 'player.vimeo.com/video')}
                    title={article.title}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                  :
                  <video controls>
                    <source src={article.video_url} type="video/mp4" />
                    {t('browserNotSupported')}
                  </video>
            )}
          </VideoContainer>
        );
        
      case 'image':
        return (
          <ImageGalleryContainer theme={theme}>
            <img 
              src={article.featured_image} 
              alt={article.title}
              className="gallery-image"
            />
            {article.excerpt && (
              <p className="image-caption">{article.excerpt}</p>
            )}
            <DownloadSection theme={theme}>
              <Button variant="primary" onClick={handleDownload}>
                <i className="fas fa-download"></i> {t('downloadImage')}
              </Button>
              <DownloadCounter theme={theme}>
                <i className="fas fa-download"></i>
                <span>{downloads} {t('downloads')}</span>
              </DownloadCounter>
            </DownloadSection>
          </ImageGalleryContainer>
        );
        
      case 'document':
        return (
          <>
            <DocumentEmbed>
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(article.document_file)}`}
                title={article.title}
              ></iframe>
            </DocumentEmbed>
            <DownloadSection theme={theme}>
              <Button variant="primary" onClick={handleDownload}>
                <i className="fas fa-download"></i> {t('downloadDocument')}
              </Button>
              <DownloadCounter theme={theme}>
                <i className="fas fa-download"></i>
                <span>{downloads} {t('downloads')}</span>
              </DownloadCounter>
            </DownloadSection>
          </>
        );
        
      default: // Texte
        return (
          <ArticleContent 
            theme={theme}
            dangerouslySetInnerHTML={{ 
              __html: article.content
            }}
          />
        );
    }
  };
  
  if (articleLoading) {
    return (
      <ArticleSection>
        <Container>
          <LoadingIndicator>
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle 
                cx="25" 
                cy="25" 
                r="20" 
                fill="none" 
                stroke={theme.primary} 
                strokeWidth="3" 
                strokeLinecap="round"
              >
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  from="0 25 25" 
                  to="360 25 25" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dasharray" 
                  from="1, 150" 
                  to="90, 150" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dashoffset" 
                  from="0" 
                  to="-35" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </LoadingIndicator>
        </Container>
      </ArticleSection>
    );
  }
  
  if (articleError) {
    return (
      <ArticleSection>
        <Container>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', color: theme.error, marginBottom: '1rem' }}>
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h2 style={{ fontSize: '1.8rem', color: theme.text, marginBottom: '1.5rem' }}>{articleError}</h2>
              <Button variant="primary" to="/blog">
                <i className="fas fa-arrow-left"></i> {t('backToBlog')}
              </Button>
            </motion.div>
          </div>
        </Container>
      </ArticleSection>
    );
  }
  
  if (!article) return null;
  
  return (
    <ArticleSection>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs theme={theme}>
            <Link to="/">{t('home')}</Link> <span>/</span> 
            <Link to="/blog">{t('blog')}</Link> <span>/</span> 
            {article.categories && article.categories.length > 0 && (
              <>
                <Link to={`/blog/category/${article.categories[0].slug}`}>{article.categories[0].name}</Link> <span>/</span>
              </>
            )}
            <span>{article.title}</span>
          </Breadcrumbs>
          
          <ShareBar theme={theme}>
            <button onClick={() => handleShare('facebook')} aria-label="Partager sur Facebook">
              <i className="fab fa-facebook-f"></i>
            </button>
            <button onClick={() => handleShare('twitter')} aria-label="Partager sur Twitter">
              <i className="fab fa-twitter"></i>
            </button>
            <button onClick={() => handleShare('linkedin')} aria-label="Partager sur LinkedIn">
              <i className="fab fa-linkedin-in"></i>
            </button>
            <button onClick={() => handleShare('whatsapp')} aria-label="Partager sur WhatsApp">
              <i className="fab fa-whatsapp"></i>
            </button>
            <button onClick={() => handleShare('email')} aria-label="Partager par email">
              <i className="fas fa-envelope"></i>
            </button>
          </ShareBar>
          
          <ArticleHeader>
            <ArticleTitle theme={theme}>{article.title}</ArticleTitle>
            
            <ArticleMeta theme={theme}>
              <div>
                <i className="fas fa-user"></i>
                <span>{article.author ? `${article.author.first_name} ${article.author.last_name || article.author.username}` : 'Admin'}</span>
              </div>
              <div>
                <i className="fas fa-calendar-alt"></i>
                <span>{formatDate(article.published_at || article.created_at)}</span>
              </div>
              {article.categories && article.categories.length > 0 && (
                <div>
                  <i className="fas fa-folder"></i>
                  <span>{article.categories.map(cat => cat.name).join(', ')}</span>
                </div>
              )}
              <div>
                <i className="fas fa-eye"></i>
                <span>{article.views || 20} {t('views')}</span>
              </div>
            </ArticleMeta>
            
            <EngagementStats theme={theme}>
              <button 
                onClick={handleLike}
                className={hasLiked ? 'active' : ''}
                aria-label={hasLiked ? "Je n'aime plus" : "J'aime"}
              >
                <i className={hasLiked ? "fas fa-heart" : "far fa-heart"}></i>
                <span>{likes} {t('likes')}</span>
              </button>
              <button 
                onClick={() => document.getElementById('comments-section').scrollIntoView({ behavior: 'smooth' })}
                aria-label="Voir les commentaires"
              >
                <i className="far fa-comment"></i>
                <span>{comments.length} {t('comments')}</span>
              </button>
              {(article.article_type === 'pdf' || article.article_type === 'document' || article.download_url) && (
                <button 
                  onClick={handleDownload}
                  aria-label="Télécharger"
                >
                  <i className="fas fa-download"></i>
                  <span>{downloads} {t('downloads')}</span>
                </button>
              )}
            </EngagementStats>
            
            {article.article_type === 'text' && article.featured_image && (
              <FeaturedImage src={article.featured_image} />
            )}
          </ArticleHeader>
          
          <SocialShare 
            title={article.title}
            hashtags={article.tags ? article.tags.map(tag => tag.name) : ['PropentaTech']}
          />
          
          {/* Contenu de l'article selon son type */}
          {renderArticleContent()}
          
          {article.tags && article.tags.length > 0 && (
            <TagsContainer>
              {article.tags.map(tag => (
                <TagItem
                  key={tag.id}
                  to={`/blog/tag/${tag.slug}`}
                  theme={theme}
                >
                  #{tag.name}
                </TagItem>
              ))}
            </TagsContainer>
          )}
          
          {article.author && (
            <AuthorBox theme={theme}>
              <AuthorImage src={article.author.profile_picture} />
              <AuthorInfo>
                <AuthorName theme={theme}>
                  {article.author.first_name} {article.author.last_name || article.author.username}
                </AuthorName>
                <AuthorBio theme={theme}>{article.author.bio || t('noAuthorBio')}</AuthorBio>
                
                {article.author.linkedin_profile && (
                  <Button
                    variant="outline"
                    size="small"
                    as="a"
                    href={article.author.linkedin_profile}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-linkedin"></i> LinkedIn
                  </Button>
                )}
              </AuthorInfo>
            </AuthorBox>
          )}
          
          {/* Section des commentaires */}
          <CommentsSection id="comments-section">
            <CommentTitle theme={theme}>{t('comments')}</CommentTitle>
            
            <CommentForm onSubmit={handleSubmitComment} theme={theme}>
              <CommentFormHeader theme={theme}>{t('leaveComment')}</CommentFormHeader>
              
              <CommentNameInput
                type="text"
                placeholder={t('yourName')}
                value={commentAuthor}
                onChange={(e) => setCommentAuthor(e.target.value)}
                theme={theme}
                required
              />
              
              <CommentInput
                placeholder={t('commentPlaceholder')}
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                theme={theme}
                required
              />
              
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? `${t('submitting')}...` : t('submitComment')}
              </Button>
            </CommentForm>
            
            <CommentList>
              {comments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: theme.text, opacity: 0.7 }}>
                  {t('noComments')}
                </div>
              ) : (
                [...comments].reverse().map(comment => (
                  <CommentCard key={comment.id} theme={theme}>
                    <CommentHeader>
                      <CommentAuthor theme={theme}>{comment.author}</CommentAuthor>
                      <CommentDate theme={theme}>{formatDate(comment.created_at)}</CommentDate>
                    </CommentHeader>
                    <CommentContent theme={theme}>{comment.content}</CommentContent>
                  </CommentCard>
                ))
              )}
            </CommentList>
          </CommentsSection>
          
          {!relatedLoading && relatedArticles?.results && relatedArticles.results.length > 0 && (
            <RelatedArticles>
              <RelatedTitle theme={theme}>{t('relatedArticles')}</RelatedTitle>
              <RelatedGrid>
                {relatedArticles.results.map((relatedArticle, index) => (
                  <ArticleCard
                    key={relatedArticle.id}
                    article={relatedArticle}
                    delay={index * 0.1}
                  />
                ))}
              </RelatedGrid>
            </RelatedArticles>
          )}
        </motion.div>
      </Container>
    </ArticleSection>
  );
};

// Composant de carte d'article (pour les articles associés)
const ArticleCard = ({ article, delay = 0 }) => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Fonction pour obtenir l'icône en fonction du type d'article
  const getArticleTypeIcon = (type) => {
    switch(type) {
      case 'pdf':
        return 'fas fa-file-pdf';
      case 'video':
        return 'fas fa-video';
      case 'image':
        return 'fas fa-image';
      case 'document':
        return 'fas fa-file-alt';
      default:
        return 'fas fa-newspaper';
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <div
        style={{
          background: theme.card.background,
          borderRadius: '8px',
          boxShadow: theme.card.shadow,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ position: 'relative' }}>
          {article.featured_image ? (
            <div
              style={{
                width: '100%',
                height: '150px',
                backgroundImage: `url(${article.featured_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '150px',
                background: theme.backgroundSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.text,
                opacity: 0.7,
              }}
            >
              <i className={getArticleTypeIcon(article.article_type)} style={{ fontSize: '3rem' }}></i>
            </div>
          )}
          
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: theme.primary,
              color: 'white',
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.7rem',
              textTransform: 'uppercase',
            }}
          >
            {article.article_type === 'text' ? t('article') : t(article.article_type)}
          </div>
        </div>
        
        <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <h3
            style={{
              fontSize: '1.2rem',
              marginBottom: '0.5rem',
              color: theme.text,
            }}
          >
            <Link 
              to={`/blog/${article.id}`}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {article.title}
            </Link>
          </h3>
          
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              fontSize: '0.8rem',
              marginBottom: '1rem',
              color: theme.text,
              opacity: 0.8,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <i className="fas fa-calendar-alt" style={{ marginRight: '0.3rem' }}></i>
              <span>{formatDate(article.published_at || article.created_at)}</span>
            </span>
            
            {article.categories && article.categories.length > 0 && (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="fas fa-folder" style={{ marginRight: '0.3rem' }}></i>
                <span>{article.categories[0].name}</span>
              </span>
            )}
          </div>
          
          <p
            style={{
              color: theme.text,
              fontSize: '0.9rem',
              marginBottom: '1rem',
              flex: 1,
            }}
          >
            {article.excerpt || `${article.content?.substring(0, 100).replace(/<[^>]*>/g, '')}...`}
          </p>
          
          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outline"
              size="small"
              to={`/blog/${article.id}`}
            >
              {t('readMore')}
            </Button>
            
            <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8rem', color: theme.text, opacity: 0.7 }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="far fa-heart" style={{ marginRight: '0.2rem' }}></i>
                <span>{article.likes || 0}</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="far fa-comment" style={{ marginRight: '0.2rem' }}></i>
                <span>{article.comments_count || 0}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ArticleDetailPage;